import { createSlice } from '@reduxjs/toolkit';
import { act } from 'react';
import { Request } from 'src/helpers';

const INITIAL_STATE = {
  initialDataFilters: null,
  // threading
  job_id: 0,
  job_mode: false,
  jobs_loaded: false,
  specialJournalReportData: {
    accFlds: [],
    showBrch: false,
    tableData: [],
    tableFields: [],
    tableTotals: {},
    numFields: [],
    tableDataWithSundry: [],
    tableDataWithNoDataFormatting: [],
    feildsInprops: {},
    settings: {}
  },
  brch: {
    required: false,
    ixBrch: 0,
    ixBrchList: [],
    label: ''
  },
  pur_terms: [],
  sales_terms: [],
  selectedReport: '',
  selectedStatus: [],
  page: 0,
  total_items: 0,
  limit: 25,
  batch: 100,
  loadedPages: 0,
  loading: false,
  loadingAll: false,
  allPagesLoaded: false,
  error: false,
  errorMessage: '',
  statusChecked: false,
  statusMessage: '',
  columnFilters: {},
  showAllSundryStatus: false,
  subAccountSettings: [],
  subAccountColumns: [],
  selectedSubAcc: [],
  formFilterData: {
    ixSPEJ: '',
    lstStatus: [],
    dt1: null,
    dt2: null,
    textValue: ''
  },
  savedJobs: [],
  serverJobs: []
};

function showSundryHelper(states, jid) {
  const copyListReport = [...states];
  const index = copyListReport.findIndex(data => data.ID === jid);
  const list = copyListReport[index];
  list.showSundry = !list.showSundry;
  copyListReport[index] = list;

  return copyListReport;
}

function showAllSundryHelper(states, payload) {
  return states.map(data => {
    return {
      ...data,
      showSundry: !payload
    };
  });
}

const slice = createSlice({
  name: 'specialJournals',
  initialState: INITIAL_STATE,
  reducers: {
    fetchInitialDataFilters(state) {
      state.error = false;
      state.errorMessage = '';
      state.statusChecked = false;
      state.statusMessage = '';
    },
    fetchInitialDataFiltersSuccess(state, action) {
      state.initialDataFilters = action.payload;
      state.loading = false;
      state.error = false;
      state.errorMessage = '';
      state.statusChecked = false;
      state.statusMessage = '';
    },
    fetchInitialDataFiltersFailed(state, action) {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.payload.message;
      state.statusChecked = false;
      state.statusMessage = '';
    },
    setServerjobs(state, action) {
      state.serverJobs = action.payload;
    },
    setSavedjobs(state, action) {
      state.savedJobs.push(action.payload);

      const MAX_SAVED = 5;
      if (state.savedJobs.length > MAX_SAVED) {
        // Remove the oldest ones to keep only the last MAX_SAVED
        state.savedJobs = state.savedJobs.slice(-MAX_SAVED);
      }
    },
    resetSavedJobs(state) {
      state.savedJobs = [];
    },
    resetServerJobs(state) {
      state.serverJobs = [];
    },
    deleteServerJob(state, action) {
      state.serverJobs = state.serverJobs.filter(
        item => item !== action.payload
      );
    },
    deleteSavedJob(state, action) {
      const payloadToDelete = action.payload;

      state.savedJobs = state.savedJobs.filter(
        job =>
          !Object.keys(payloadToDelete).every(
            key => job[key] === payloadToDelete[key]
          )
      );
    },
    deleteSavedJobById(state, action) {
      const idToDelete = action.payload; // just the job id
      state.savedJobs = state.savedJobs.filter(job => job.id !== idToDelete);
    },
    setSelectedReport(state, action) {
      state.selectedReport = action.payload;
      state.formFilterData = {
        ...state.formFilterData,
        ixSPEJ: action.payload
      };
    },
    setSubAccountSettings(state, action) {
      state.subAccountSettings = action.payload;
    },
    setSubAccountColumns(state, action) {
      state.subAccountColumns = action.payload;
    },
    setSelectedSubAcc(state, action) {
      const { index, value } = action.payload;
      state.selectedSubAcc[index] = value;
    },
    resetSelectedSubAcc(state, action) {
      state.selectedSubAcc = [];
    },
    setSelectedStatus(state, action) {
      state.selectedStatus = action.payload;
      state.formFilterData = {
        ...state.formFilterData,
        lstStatus: action.payload
      };
    },
    setBatch(state, action) {
      state.batch = action.payload.batch;
    },
    setDateRange(state, action) {
      state.formFilterData = {
        ...state.formFilterData,
        dt1: action.payload.dt1,
        dt2: action.payload.dt2,
        textValue: action.payload.textValue
      };
    },
    clearSpecialJournalReport(state) {
      return {
        ...state,
        ...INITIAL_STATE,
        brch: {
          ...state.brch,
          ixBrch: 0
        }
      };
    },
    setSpecialJournalReportError(state, action) {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.payload.message;
    },
    setSpecialJournalReportStatus(state, action) {
      const { message, statusChecked } = action.payload ?? {};

      // Update message only if different
      const messageChanged =
        message !== undefined && state.statusMessage !== message;
      if (messageChanged) {
        state.statusMessage = message;
      }

      // Update statusChecked if included AND different
      if (statusChecked !== undefined) {
        if (state.statusChecked !== statusChecked) {
          state.statusChecked = statusChecked;
        }
      }
      // If message changed but statusChecked was NOT provided → default to true
      else if (messageChanged && state.statusChecked !== true) {
        state.statusChecked = true;
      }
    },
    setSpecialJournalReportData(state, action) {
      state.page = 0;
      state.limit = 25;
      state.loading = false;
      state.showAllSundryStatus = false;
      state.specialJournalReportData = {
        ...state.specialJournalReportData,
        ...(action.payload?.data || [])
      };
      state.columnFilters = action.payload?.settings?.allowed_columns || {};
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setLoadingAll(state, action) {
      state.loadingAll = action.payload;
    },
    setSpecialJournalReportDataByPage(state, action) {
      const incoming = action.payload?.data || {};

      state.page = 0;
      state.limit = 25;
      state.loading = false;
      state.showAllSundryStatus = false;

      state.specialJournalReportData = {
        accFlds: incoming.accFlds || state.specialJournalReportData.accFlds,
        settings: incoming.settings || state.specialJournalReportData.settings,
        tableData: [
          ...(state.specialJournalReportData.tableData || []),
          ...(incoming.tableData || [])
        ],
        showBrch: incoming?.settings?.showBrch ?? false,
        tableFields:
          incoming.tableFields || state.specialJournalReportData.tableFields,
        tableTotals: {
          ...state.specialJournalReportData.tableTotals,
          ...(incoming.tableTotals || {})
        },
        numFields:
          incoming.numFields || state.specialJournalReportData.numFields,
        tableDataWithSundry: [
          ...(state.specialJournalReportData.tableDataWithSundry || []),
          ...(incoming.tableDataWithSundry || [])
        ],
        tableDataWithNoDataFormatting: [
          ...(state.specialJournalReportData.tableDataWithNoDataFormatting ||
            []),
          ...(incoming.tableDataWithNoDataFormatting || [])
        ],
        feildsInprops: {
          ...state.specialJournalReportData.feildsInprops,
          ...(incoming.feildsInprops || {})
        }
      };

      state.columnFilters = action.payload?.settings?.allowed_columns || {};
    },
    setColumnFilters(state, action) {
      state.columnFilters = action.payload;
    },
    setJobId(state, action) {
      state.job_id = action.payload;
    },
    setTotalItems(state, action) {
      state.total_items = action.payload;
    },
    clearSpecialJournalReportData(state) {
      state.job_id = 0;
      state.job_mode = false;

      state.error = false;

      state.page = 0;
      state.jobs_loaded = false;
      state.limit = 25;
      state.columnFilters = {};
      state.specialJournalReportData = {
        accFlds: [],
        tableData: [],
        settings: {},
        showBrch: false,
        tableFields: [],
        tableTotals: {},
        numFields: [],
        tableDataWithNoDataFormatting: [],
        tableDataWithSundry: [],
        feildsInprops: {}
      };
    },
    // Case - 2025-06-002558
    // https://ulap.biz/app/trans/kXGBq
    //
    // Clearing of SPEJ data but keeping job_id and job_mode on
    clearSPEJOnlyData(state) {
      state.error = false;
      state.page = 0;
      state.limit = 25;
      state.columnFilters = {};
      state.specialJournalReportData = {
        accFlds: [],
        tableData: [],
        settings: {},
        showBrch: false,
        tableFields: [],
        tableTotals: {},
        numFields: [],
        tableDataWithNoDataFormatting: [],
        tableDataWithSundry: [],
        feildsInprops: {}
      };
    },

    fetchSpecialJournalReportData(state) {
      // state.loading = true;
      state.error = false;
      state.errorMessage = '';
      state.statusChecked = false;
      // state.statusMessage = '';
    },
    setJobMode(state, action) {
      state.job_mode = action.payload;
    },
    setJobLoaded(state, action) {
      state.jobs_loaded = action.payload;
    },
    setReportPage(state, action) {
      state.page = action.payload;
    },
    setReportLimit(state, action) {
      state.limit = action.payload;
      state.page = 0;
    },
    changeShowSundryStatus(state, action) {
      state.specialJournalReportData = {
        ...state.specialJournalReportData,
        tableData: showSundryHelper(
          state.specialJournalReportData.tableData,
          action.payload
        )
      };
    },
    showAllSundry(state, action) {
      state.showAllSundryStatus = !state.showAllSundryStatus;
      state.specialJournalReportData = {
        ...state.specialJournalReportData,
        tableData: showAllSundryHelper(
          state.specialJournalReportData.tableData,
          action.payload
        )
      };
    },
    columnChangeStatus(state, action) {
      state.columnFilters = {
        ...state.columnFilters,
        [action.payload.key]: action.payload.value
      };
    },
    setBranchSpecialJournalReport(state, action) {
      state.brch = {
        ...state.brch,
        ixBrch: Array.isArray(action.payload)
          ? action.payload.length > 0
            ? action.payload[0]
            : 0
          : action.payload,
        ixBrchList: Array.isArray(action.payload) ? action.payload : []
      };
    },
    incLoadedPages(state, action) {
      state.loadedPages += 1;
    },
    reduceLoadedPages(state, action) {
      state.loadedPages -= 1;
      state.loading = false;
      state.showAllSundryStatus = false;
    },
    resetLoadedPages(state, action) {
      state.loadedPages = 0;
    },
    brchRequiredSpecialJournalReport(state, action) {
      state.brch = {
        ...state.brch,
        label: action.payload.label,
        required: action.payload.required
      };
    },
    setPurTerms(state, action) {
      state.pur_terms = action.payload;
    },
    setSalesTerms(state, action) {
      state.sales_terms = action.payload;
    },
    cancelRequestSpej(state) {
      state.loading = false;
    },
    clear() {
      return INITIAL_STATE;
    },
    restore(_, action) {
      return action.payload || INITIAL_STATE;
    },
    setAllPagesLoaded: (state, action) => {
      state.allPagesLoaded = action.payload;
    }
  }
});

const specialJournalsReducer = slice.reducer;

export const {
  fetchInitialDataFilters,
  fetchInitialDataFiltersSuccess,
  fetchInitialDataFiltersFailed,
  setSelectedReport,
  setSelectedStatus,
  setDateRange,
  clearSpecialJournalReport,
  clearSPEJOnlyData,

  setSpecialJournalReportError,
  setSpecialJournalReportStatus,
  setSpecialJournalReportData,
  setSpecialJournalReportDataByPage,
  setColumnFilters,
  clearSpecialJournalReportData,
  fetchSpecialJournalReportData,
  setReportPage,
  setReportLimit,
  changeShowSundryStatus,
  showAllSundry,
  columnChangeStatus,
  setBranchSpecialJournalReport,
  brchRequiredSpecialJournalReport,
  cancelRequestSpej,
  setPurTerms,
  setSalesTerms,
  clear,
  restore,
  setBatch,
  setAllPagesLoaded,
  setTotalItems,
  incLoadedPages,
  reduceLoadedPages,
  resetLoadedPages,
  setJobId,
  setSavedjobs,
  resetSavedJobs,
  deleteSavedJob,
  deleteSavedJobById,
  setServerjobs,
  deleteServerJob,
  resetServerJobs,

  setLoading,
  setLoadingAll,
  setJobMode,
  setJobLoaded,
  setSubAccountSettings,
  setSubAccountColumns,
  setSelectedSubAcc,
  resetSelectedSubAcc
} = slice.actions;
export default specialJournalsReducer;

export const fetchInitialData = () => async (dispatch, getState) => {
  const { base_url, userToken } = getState().auth;
  const request = new Request(base_url, {
    headers: {
      'x-access-tokens': userToken,
      'Content-Type': 'application/json'
    }
  });

  dispatch(fetchInitialDataFilters());
  const [meta, pur, sls] = await request.multiFetch([
    {
      url: '/reports/special/journal/init',
      method: 'get'
    },
    {
      url: '/lib/terms/pur',
      method: 'post'
    },
    {
      url: '/lib/terms/sales',
      method: 'post'
    }
  ]);

  if (!meta?.success) {
    dispatch(
      fetchInitialDataFiltersFailed({
        message: meta?.data ?? 'Something went wrong.'
      })
    );
  } else dispatch(fetchInitialDataFiltersSuccess(meta.data));

  if (pur.success) dispatch(slice.actions.setPurTerms(pur?.data ?? []));

  if (sls.success) dispatch(slice.actions.setSalesTerms(sls?.data ?? []));
};
